import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface JobSkill {
  skillId: string;
  skillName: string;
  skillType: 'technical' | 'soft';
  category: 'required_technical' | 'good_to_have_technical' | 'soft';
  yearsExperience?: number;
}

interface JobSkillsSectionProps {
  skills: any[];
  jobSkills: JobSkill[];
  onSkillsChange: (skills: JobSkill[]) => void;
  onSkillsRefresh: () => void;
}

const EXPERIENCE_OPTIONS = [
  { value: '1', label: '1+ year' },
  { value: '2', label: '2+ years' },
  { value: '3', label: '3+ years' },
  { value: '5', label: '5+ years' },
  { value: '7', label: '7+ years' },
  { value: '10', label: '10+ years' },
];

const JobSkillsSection = ({
  skills,
  jobSkills,
  onSkillsChange,
  onSkillsRefresh
}: JobSkillsSectionProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [newSkillName, setNewSkillName] = useState('');
  const [showSkillPicker, setShowSkillPicker] = useState<'required_technical' | 'good_to_have_technical' | 'soft' | null>(null);
  const { toast } = useToast();

  const requiredTechnical = jobSkills.filter(s => s.category === 'required_technical');
  const goodToHave = jobSkills.filter(s => s.category === 'good_to_have_technical');
  const softSkills = jobSkills.filter(s => s.category === 'soft');

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
    const notSelected = !jobSkills.some(js => js.skillId === skill.id);
    return matchesSearch && notSelected;
  });

  const addSkill = (skill: any, category: 'required_technical' | 'good_to_have_technical' | 'soft') => {
    const newSkill: JobSkill = {
      skillId: skill.id,
      skillName: skill.name,
      skillType: skill.type || 'technical',
      category,
      yearsExperience: undefined
    };
    onSkillsChange([...jobSkills, newSkill]);
    setShowSkillPicker(null);
    setSearchTerm('');
  };

  const removeSkill = (skillId: string) => {
    onSkillsChange(jobSkills.filter(s => s.skillId !== skillId));
  };

  const updateExperience = (skillId: string, years: number | undefined) => {
    onSkillsChange(
      jobSkills.map(s =>
        s.skillId === skillId ? { ...s, yearsExperience: years } : s
      )
    );
  };

  const handleAddNewSkill = async (category: 'required_technical' | 'good_to_have_technical' | 'soft') => {
    if (!newSkillName.trim()) {
      toast({
        title: 'Skill name required',
        description: 'Please enter a skill name',
        variant: 'destructive'
      });
      return;
    }

    try {
      const skillType = category === 'soft' ? 'soft' : 'technical';
      const { data, error } = await supabase
        .from('skills')
        .insert([{ name: newSkillName.trim(), type: skillType }])
        .select()
        .single();

      if (error) throw error;

      onSkillsRefresh();
      addSkill(data, category);
      setNewSkillName('');
      toast({ title: 'Skill added successfully' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const renderSkillPicker = (category: 'required_technical' | 'good_to_have_technical' | 'soft') => {
    const isOpen = showSkillPicker === category;
    const categoryLabel = category === 'required_technical' 
      ? 'Required Technical' 
      : category === 'good_to_have_technical' 
        ? 'Good to Have' 
        : 'Soft';
    const skillTypeFilter = category === 'soft' ? 'soft' : 'technical';

    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowSkillPicker(isOpen ? null : category)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add from list
          </Button>
        </div>

        {isOpen && (
          <Card className="p-3 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="max-h-40 overflow-y-auto space-y-1">
              {filteredSkills
                .filter(s => category === 'soft' ? s.type === 'soft' : s.type !== 'soft')
                .slice(0, 20)
                .map(skill => (
                  <div
                    key={skill.id}
                    className="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer"
                    onClick={() => addSkill(skill, category)}
                  >
                    <span className="text-sm">{skill.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {skill.type}
                    </Badge>
                  </div>
                ))}
              {filteredSkills.filter(s => category === 'soft' ? s.type === 'soft' : s.type !== 'soft').length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No matching skills found
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <Input
                placeholder={`Add new ${categoryLabel.toLowerCase()} skill...`}
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNewSkill(category);
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleAddNewSkill(category)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Required Technical Skills */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Required Technical Skills</Label>
          <span className="text-xs text-muted-foreground">With years of experience</span>
        </div>
        
        <div className="border rounded-md p-3 space-y-2 min-h-[60px]">
          {requiredTechnical.length > 0 ? (
            <div className="space-y-2">
              {requiredTechnical.map(skill => (
                <div key={skill.skillId} className="flex items-center gap-2 bg-muted/50 p-2 rounded-md">
                  <Badge variant="default" className="flex-shrink-0">
                    {skill.skillName}
                  </Badge>
                  <Select
                    value={skill.yearsExperience?.toString() || 'any'}
                    onValueChange={(value) => updateExperience(skill.skillId, value === 'any' ? undefined : parseInt(value))}
                  >
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue placeholder="Experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      {EXPERIENCE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkill(skill.skillId)}
                    className="h-8 w-8 p-0 ml-auto"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No required technical skills added
            </p>
          )}
        </div>
        {renderSkillPicker('required_technical')}
      </div>

      {/* Good to Have Technical Skills */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Good to Have (Technical)</Label>
        
        <div className="border rounded-md p-3 min-h-[60px]">
          {goodToHave.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {goodToHave.map(skill => (
                <Badge
                  key={skill.skillId}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                >
                  {skill.skillName}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkill(skill.skillId)}
                    className="h-4 w-4 p-0 hover:bg-transparent"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No good to have skills added
            </p>
          )}
        </div>
        {renderSkillPicker('good_to_have_technical')}
      </div>

      {/* Soft Skills */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Soft Skills</Label>
        
        <div className="border rounded-md p-3 min-h-[60px]">
          {softSkills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {softSkills.map(skill => (
                <Badge
                  key={skill.skillId}
                  variant="outline"
                  className="flex items-center gap-1 pr-1"
                >
                  {skill.skillName}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkill(skill.skillId)}
                    className="h-4 w-4 p-0 hover:bg-transparent"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No soft skills added
            </p>
          )}
        </div>
        {renderSkillPicker('soft')}
      </div>
    </div>
  );
};

export default JobSkillsSection;
